import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiClient from "../../services/axiosClient";
import qs from "qs";

const getErrorMessage = (error) =>
  error.response?.data?.message ||
  error.response?.data ||
  error.message ||
  "An error occurred";

/* ============== Async Thunks ============== */

export const fetchUniqueUsersList = createAsyncThunk(
  "dashboard/fetchUniqueUsersList",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiClient.get("/get-unique-users-list", {
        params,
        paramsSerializer: (params) =>
          qs.stringify(params, { arrayFormat: "repeat" }),
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const fetchBuCount = createAsyncThunk(
  "dashboard/fetchBuCount",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiClient.get("/get-bu-type-count", {
        params,
        paramsSerializer: (params) =>
          qs.stringify(params, { arrayFormat: "repeat" }),
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const fetchCardData = createAsyncThunk(
  "dashboard/fetchCardData",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiClient.get("/users-count-cards", {
        params,
        paramsSerializer: (params) =>
          qs.stringify(params, { arrayFormat: "repeat" }),
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const fetchUserInteraction = createAsyncThunk(
  "dashboard/fetchUserInteraction",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiClient.get("/get-user-session-stats", {
        params,
        paramsSerializer: (params) =>
          qs.stringify(params, { arrayFormat: "repeat" }),
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const fetchWeeklyUsers = createAsyncThunk(
  "dashboard/fetchWeeklyUsers",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiClient.get("/get-highest-lowest-users", {
        params,
        paramsSerializer: (params) =>
          qs.stringify(params, { arrayFormat: "repeat" }),
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const fetchQuestionDist = createAsyncThunk(
  "dashboard/fetchQuestionDist",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(
        "/get-question-type-classification",
        {
          params,
          paramsSerializer: (params) =>
            qs.stringify(params, { arrayFormat: "repeat" }),
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const fetchFeedbackCount = createAsyncThunk(
  "dashboard/fetchFeedbackCount",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiClient.get("/get-feedback-count", {
        params,
        paramsSerializer: (params) =>
          qs.stringify(params, { arrayFormat: "repeat" }),
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const fetchFeedbackDist = createAsyncThunk(
  "dashboard/fetchFeedbackDist",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiClient.get("/get-feedback-type-data", {
        params,
        paramsSerializer: (params) =>
          qs.stringify(params, { arrayFormat: "repeat" }),
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);


export const fetchAgentMap = createAsyncThunk(
  "dashboard/fetchAgentMap",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get("/get-sag-bu-mapping");
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/* ============== Slice with Your Exact Loading Names ============== */

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState: {
    // Data
    usersList: null,
    cardData: null,
    buCount: null,
    userInteraction: null,
    weeklyUsers: null,
    questionDist: null,
    feedbackCount: null,
    feedbackDist: null,
    agentMap: null,       


    // Your exact loading variable names
    usersListFetching: false,
    buCountFetching: false,
    cardDataFetching: false,
    userInteractionFetching: false,
    weeklyUsersFetching: false,
    questionDistFetching: false,
    feedbackCountFetching: false,
    feedbackDistFetching: false,
    agentMapFetching: false,

    // Optional: individual errors (you can remove if not needed)
    error: null,
  },
  reducers: {
    clearDashboardData: (state) => {
      state.usersList = null;
      state.cardData = null;
      state.userInteraction = null;
      state.buCount = null;
      state.weeklyUsers = null;
      state.questionDist = null;
      state.feedbackCount = null;
      state.feedbackDist = null;
      state.error = null;
    },
    clearDashboardError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Users List
      .addCase(fetchUniqueUsersList.pending, (state) => {
        state.usersListFetching = true;
        state.error = null;
      })
      .addCase(fetchUniqueUsersList.fulfilled, (state, action) => {
        state.usersListFetching = false;
        state.usersList = action.payload;
      })
      .addCase(fetchUniqueUsersList.rejected, (state, action) => {
        state.usersListFetching = false;
        state.error = action.payload;
      })

      .addCase(fetchBuCount.pending, (state) => {
        state.buCountFetching = true;
        state.error = null;
      })
      .addCase(fetchBuCount.fulfilled, (state, action) => {
        state.buCountFetching = false;
        state.buCount = action.payload;
      })
      .addCase(fetchBuCount.rejected, (state, action) => {
        state.buCountFetching = false;
        state.error = action.payload;
      })

      // Card Data
      .addCase(fetchCardData.pending, (state) => {
        state.cardDataFetching = true;
        state.error = null;
      })
      .addCase(fetchCardData.fulfilled, (state, action) => {
        state.cardDataFetching = false;
        state.cardData = action.payload;
      })
      .addCase(fetchCardData.rejected, (state, action) => {
        state.cardDataFetching = false;
        state.error = action.payload;
      })

      // User Interaction
      .addCase(fetchUserInteraction.pending, (state) => {
        state.userInteractionFetching = true;
        state.error = null;
      })
      .addCase(fetchUserInteraction.fulfilled, (state, action) => {
        state.userInteractionFetching = false;
        state.userInteraction = action.payload;
      })
      .addCase(fetchUserInteraction.rejected, (state, action) => {
        state.userInteractionFetching = false;
        state.error = action.payload;
      })

      // Weekly Users
      .addCase(fetchWeeklyUsers.pending, (state) => {
        state.weeklyUsersFetching = true;
        state.error = null;
      })
      .addCase(fetchWeeklyUsers.fulfilled, (state, action) => {
        state.weeklyUsersFetching = false;
        state.weeklyUsers = action.payload;
      })
      .addCase(fetchWeeklyUsers.rejected, (state, action) => {
        state.weeklyUsersFetching = false;
        state.error = action.payload;
      })

      // Question Distribution
      .addCase(fetchQuestionDist.pending, (state) => {
        state.questionDistFetching = true;
        state.error = null;
      })
      .addCase(fetchQuestionDist.fulfilled, (state, action) => {
        state.questionDistFetching = false;
        state.questionDist = action.payload;
      })
      .addCase(fetchQuestionDist.rejected, (state, action) => {
        state.questionDistFetching = false;
        state.error = action.payload;
      })

      // Feedback Count
      .addCase(fetchFeedbackCount.pending, (state) => {
        state.feedbackCountFetching = true;
        state.error = null;
      })
      .addCase(fetchFeedbackCount.fulfilled, (state, action) => {
        state.feedbackCountFetching = false;
        state.feedbackCount = action.payload;
      })
      .addCase(fetchFeedbackCount.rejected, (state, action) => {
        state.feedbackCountFetching = false;
        state.error = action.payload;
      })



      .addCase(fetchAgentMap.pending, (state) => {
  state.agentMapFetching = true;
})
.addCase(fetchAgentMap.fulfilled, (state, action) => {
  state.agentMapFetching = false;
  state.agentMap = action.payload;
})
.addCase(fetchAgentMap.rejected, (state, action) => {
  state.agentMapFetching = false;
  state.error = action.payload;
})

      // Feedback Distribution
      .addCase(fetchFeedbackDist.pending, (state) => {
        state.feedbackDistFetching = true;
        state.error = null;
      })
      .addCase(fetchFeedbackDist.fulfilled, (state, action) => {
        state.feedbackDistFetching = false;
        state.feedbackDist = action.payload;
      })
      .addCase(fetchFeedbackDist.rejected, (state, action) => {
        state.feedbackDistFetching = false;
        state.error = action.payload;
      });



      
  },
});

export const { clearDashboardData, clearDashboardError } =
  dashboardSlice.actions;
export default dashboardSlice.reducer;
